import React, { useEffect, useRef, useState } from 'react';
import API from '../api';
import styles from './About.module.css';
import aboutImage from '../images/About.jpg';
import PageHeading from '../components/PageHeading';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';

const About = () => {
  const { user } = useAuth();
  const [text, setText] = useState('');
  const [html, setHtml] = useState('');
  const [format, setFormat] = useState('text');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [draftHtml, setDraftHtml] = useState('');
  const [draftFormat, setDraftFormat] = useState('text');
  const [colorPick, setColorPick] = useState('#F9DE4B');
  const [sizePick, setSizePick] = useState(20);
  const [fontPick, setFontPick] = useState('');
  const textAreaRef = useRef(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await API.get('/content', { params: { page: 'About', section: 'introText' } });
        setText(res.data.text || '');
        setHtml(res.data.html || '');
        setFormat(res.data.format || 'text');
      } catch (_) {}
    }
    load();
  }, []);

  const isAdmin = !!user && user.role === 'admin';

  const escapeHtml = (unsafe) => {
    return unsafe
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  const renderFormattedText = (plainText) => {
    // Escape any HTML first
    let safe = escapeHtml(plainText || '');
    // Bold: **text**
    safe = safe.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    // Size: [size=24]text[/size] → constrain size 8..72
    safe = safe.replace(/\[size=(\d{1,3})\]([\s\S]*?)\[\/size\]/g, (m, p1, p2) => {
      const size = Math.max(8, Math.min(72, parseInt(p1, 10) || 16));
      return `<span style="font-size:${size}px">${p2}</span>`;
    });
    // Color: [color=#hex|name]text[/color]
    safe = safe.replace(/\[color=([#a-zA-Z0-9]+)\]([\s\S]*?)\[\/color\]/g, (m, p1, p2) => {
      const color = String(p1).slice(0, 32);
      return `<span style="color:${color}">${p2}</span>`;
    });
    // Lists: [ul]...[/ul], [ol]...[/ol], [li]...[/li]
    safe = safe
      .replace(/\[ul\]/g, '<ul>')
      .replace(/\[\/ul\]/g, '</ul>')
      .replace(/\[ol\]/g, '<ol>')
      .replace(/\[\/ol\]/g, '</ol>')
      .replace(/\[li\]/g, '<li>')
      .replace(/\[\/li\]/g, '</li>');
    // Inline font: [font=Bebas Neue]text[/font]
    safe = safe.replace(/\[font=([a-zA-Z\s\-]+)\]([\s\S]*?)\[\/font\]/g, (m, p1, p2) => {
      const name = String(p1).trim();
      let family = '';
      if (name === 'Bebas Neue') family = "'Bebas Neue', sans-serif";
      else if (name === 'Barlow') family = "'Barlow', sans-serif";
      else if (name === 'sans-serif' || name === 'Sans-serif') family = 'sans-serif';
      else family = name; // fallback
      return `<span style="font-family:${family}">${p2}</span>`;
    });
    // Line breaks
    safe = safe.replace(/\n/g, '<br />');
    return { __html: safe };
  };

  const wrapSelection = (before, after) => {
    const el = textAreaRef.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const value = draft;
    const selected = value.slice(start, end) || 'text';
    const next = value.slice(0, start) + before + selected + after + value.slice(end);
    setDraft(next);
    // restore selection around inserted text
    setTimeout(() => {
      const newStart = start + before.length;
      const newEnd = newStart + selected.length;
      el.focus();
      el.setSelectionRange(newStart, newEnd);
    }, 0);
  };

  const applyBold = () => wrapSelection('**', '**');
  const applyColor = () => wrapSelection(`[color=${colorPick}]`, '[/color]');
  const applySize = () => {
    const px = Math.max(8, Math.min(72, parseInt(sizePick, 10) || 16));
    wrapSelection(`[size=${px}]`, '[/size]');
  };
  const applyCombo = () => {
    const px = Math.max(8, Math.min(72, parseInt(sizePick, 10) || 16));
    // nest size inside color to allow both
    const before = `[color=${colorPick}][size=${px}]`;
    const after = '[/size][/color]';
    wrapSelection(before, after);
  };
  const applyFont = () => {
    const name = fontPick || 'sans-serif';
    wrapSelection(`[font=${name}]`, '[/font]');
  };
  const applyUl = () => wrapSelection('[ul]\n', '\n[/ul]');
  const applyOl = () => wrapSelection('[ol]\n', '\n[/ol]');
  const applyLi = () => wrapSelection('[li]', '[/li]');
  return (
    // This wrapper now controls the entire page layout
    <div className={styles.pageContainer}>
      {/* HEADER IS NOW A SIBLING TO MAIN */}
      <Header />
      {/* Background section sits behind header and content on desktop */}
      <div className={styles.leftSection}>
        <img className={styles.backgroundImageLeft} src={aboutImage} alt="" />
        <div className={styles.imageOverlayLeft} />
      </div>

      {/* MAIN CONTENT AREA */}
      <main className={styles.aboutContainer}>
        <div className={styles.contentLayer}>
          <PageHeading>LET US INTRODUCE OURSELVES</PageHeading>
          <div className={styles.introText}>
          <div className={styles.contentBlock}>
            {editing ? (
              <>
                <div style={{ marginBottom: '0.5rem' }}>
                  <label style={{ marginRight: '1rem' }}>
                    <input type="radio" name="fmt" value="text" checked={draftFormat === 'text'} onChange={() => setDraftFormat('text')} /> Text
                  </label>
                  <label>
                    <input type="radio" name="fmt" value="html" checked={draftFormat === 'html'} onChange={() => setDraftFormat('html')} /> HTML
                  </label>
                </div>
                {draftFormat === 'text' ? (
                  <textarea
                    ref={textAreaRef}
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    rows={24}
                    style={{ width: '100%', fontFamily: 'monospace', marginBottom: '0.5rem', minHeight: '50vh', resize: 'vertical' }}
                    placeholder="Enter text"
                  />
                ) : (
                  <textarea
                    value={draftHtml}
                    onChange={e => setDraftHtml(e.target.value)}
                    rows={24}
                    style={{ width: '100%', fontFamily: 'monospace', marginBottom: '0.5rem', minHeight: '50vh', resize: 'vertical' }}
                    placeholder="Enter HTML (supports bold, color, size via inline styles or tags)"
                  />
                )}
                {draftFormat === 'text' && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <button type="button" className="btn btn-outline" onClick={applyBold}>Bold</button>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                      <label>Color</label>
                      <input type="color" value={colorPick} onChange={e => setColorPick(e.target.value)} />
                      <button type="button" className="btn btn-outline" onClick={applyColor}>Apply Color</button>
                    </div>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                      <label>Size</label>
                      <input type="number" min={8} max={72} step={1} value={sizePick} onChange={e => setSizePick(e.target.value)} style={{ width: 70 }} />
                      <button type="button" className="btn btn-outline" onClick={applySize}>Apply Size</button>
                    </div>
                    <button type="button" className="btn btn-outline" onClick={applyCombo}>Color + Size</button>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                      <label>Font</label>
                      <select value={fontPick} onChange={e => setFontPick(e.target.value)}>
                        <option value="">Default</option>
                        <option value="Bebas Neue">Bebas Neue</option>
                        <option value="Barlow">Barlow</option>
                        <option value="sans-serif">Sans-serif</option>
                      </select>
                      <button type="button" className="btn btn-outline" onClick={applyFont}>Apply Font</button>
                    </div>
                    <span style={{ width: 1, height: 20, background: '#555' }} />
                    <button type="button" className="btn btn-outline" onClick={applyUl}>UL</button>
                    <button type="button" className="btn btn-outline" onClick={applyOl}>OL</button>
                    <button type="button" className="btn btn-outline" onClick={applyLi}>LI</button>
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    className="btn btn-primary"
                    onClick={async () => {
                      await API.put('/admin/content', { page: 'About', section: 'introText', text: draftFormat === 'text' ? draft : '', html: draftFormat === 'html' ? draftHtml : '', format: draftFormat });
                      setText(draftFormat === 'text' ? draft : '');
                      setHtml(draftFormat === 'html' ? draftHtml : '');
                      setFormat(draftFormat);
                      setEditing(false);
                    }}
                  >Save</button>
                  <button className="btn btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
                </div>
              </>
            ) : (
              format === 'html' && html ? (
                <div dangerouslySetInnerHTML={{ __html: html }} />
              ) : text ? (
                <div dangerouslySetInnerHTML={renderFormattedText(text)} />
              ) : null
            )}
            {isAdmin && !editing && (
              <div style={{ marginTop: '1rem' }}>
                <button className="btn btn-outline" onClick={() => { setDraft(text); setDraftHtml(html); setDraftFormat(format); setEditing(true); }}>Edit</button>
              </div>
            )}
            {isAdmin && editing && draftFormat === 'text' && (
              <div style={{ marginTop: '0.5rem', fontSize: 12, opacity: 0.8 }}>
                Supported: **bold**, [size=24]big text[/size], [color=#F9DE4B]yellow[/color]
              </div>
            )}
            </div>
          </div>
        </div>
      </main>
      
      {/* FOOTER */}
      <Footer />
    </div>
  );
};

export default About;